/* Real holiday price-impact calculation — Fase B of the Forecast v2
   roadmap (CLAUDE.md "FOB PRICE FORECAST v2" → Fase B: "Holiday amplifier
   basado en spike histórico por holiday × región"). No AI, pure rules —
   per the roadmap's explicit "Todo esto sin AI, reglas puras".

   HolidayImpactRegistry.js originally shipped (fix-dashboard.md Fase C.3)
   with hand-guessed placeholder magnitudes, explicitly commented as
   "PLACEHOLDER ESTIMATES... will be replaced by real USDA-derived
   historical averages once the Market News API is active" — that trigger
   condition is now true (7-year USDA backfill, 2026-09-15 Session Log).
   This file computes the real replacement from historical_prices.

   METHOD (event-study style, not a naive year-average comparison): for
   each holiday occurrence, compare the pre-window (-14 to -1 days) and
   post-window (0 to +14 days) average price against a "shoulder" baseline
   — the average of [-45,-15] and [+15,+45] days around the SAME holiday
   occurrence. Comparing against nearby shoulder weeks (not the whole
   year) isolates the holiday-specific effect from ordinary seasonal
   price drift that has nothing to do with the holiday (e.g. Christmas
   sitting inside December's normal seasonal low would otherwise look
   like a huge "holiday dip" that's really just December being December).
   Effects are averaged across every year with usable data on both sides,
   giving a real historical average magnitude per holiday × product. */

function pad2(n) { return n < 10 ? "0" + n : String(n); }
function toISO(d) { return d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate()); }
function addDays(dateStr, delta) {
  var d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return toISO(d);
}
/* nth (1-based) weekday-of-month, e.g. nthWeekday(2026, 1, 1, 3) = 3rd
   Monday of January 2026 (weekday: 0=Sun..6=Sat). */
function nthWeekday(year, month, weekday, n) {
  var d = new Date(year, month - 1, 1);
  var offset = (weekday - d.getDay() + 7) % 7;
  d.setDate(1 + offset + (n - 1) * 7);
  return toISO(d);
}
function lastWeekday(year, month, weekday) {
  var d = new Date(year, month, 0); // last day of month
  var offset = (d.getDay() - weekday + 7) % 7;
  d.setDate(d.getDate() - offset);
  return toISO(d);
}

/* Same 11 holidays/keys as HolidayImpactRegistry.js's hardcoded 2026-2027
   list — computed here for ANY year via the actual US federal-holiday
   scheduling rules, so the 7-year backfill (2019-2026) can be matched
   against real occurrences instead of only the 2 hardcoded years. */
export function computeHolidayDate(key, year) {
  switch (key) {
    case "new_years": return year + "-01-01";
    case "mlk": return nthWeekday(year, 1, 1, 3);
    case "presidents": return nthWeekday(year, 2, 1, 3);
    case "memorial": return lastWeekday(year, 5, 1);
    case "juneteenth": return year + "-06-19";
    case "independence": return year + "-07-04";
    case "labor": return nthWeekday(year, 9, 1, 1);
    case "columbus": return nthWeekday(year, 10, 1, 2);
    case "veterans": return year + "-11-11";
    case "thanksgiving": return nthWeekday(year, 11, 4, 4);
    case "christmas": return year + "-12-25";
    default: return null;
  }
}

export var HOLIDAY_KEYS = ["new_years", "mlk", "presidents", "memorial", "juneteenth", "independence", "labor", "columbus", "veterans", "thanksgiving", "christmas"];

function average(nums) { return nums.length ? nums.reduce(function (s, v) { return s + v; }, 0) / nums.length : null; }
function round1(n) { return Math.round(n * 10) / 10; }

/* Mean price (low/high average) of USDA historical_prices rows for
   `product` whose reportDate falls in [from, to] inclusive. Returns null
   if no rows match (caller treats that as "not enough data"). */
function windowAvgPrice(rowsByDate, from, to) {
  var vals = [];
  Object.keys(rowsByDate).forEach(function (date) {
    if (date >= from && date <= to) vals = vals.concat(rowsByDate[date]);
  });
  return vals.length ? average(vals) : null;
}

/* historicalRows: app-shape historical_prices rows (source/product/
   priceLow/priceHigh/reportDate), already filtered to ONE source (USDA —
   the only one with 7 years of history across all 3 products; SNIIM only
   covers Blackberry) by the caller. Returns, for each holiday key:
   { spikePct, dipPct, yearsUsed } using real computed magnitudes, or
   null when fewer than MIN_YEARS occurrences have usable data on both
   sides — caller falls back to the old placeholder estimate for that
   specific holiday in that case (a few small-sample holidays,
   particularly Juneteenth pre-2021 when it wasn't yet a recognized
   federal holiday, may never accumulate enough real data). */
export function computeRealHolidayImpact(historicalRows, product) {
  var MIN_YEARS = 2;
  var rowsByDate = {};
  (historicalRows || []).forEach(function (r) {
    if (!r || r.product !== product || !r.reportDate) return;
    var mid = (r.priceLow != null && r.priceHigh != null) ? (parseFloat(r.priceLow) + parseFloat(r.priceHigh)) / 2
      : (r.priceLow != null ? parseFloat(r.priceLow) : (r.priceHigh != null ? parseFloat(r.priceHigh) : null));
    if (mid == null || isNaN(mid)) return;
    if (!rowsByDate[r.reportDate]) rowsByDate[r.reportDate] = [];
    rowsByDate[r.reportDate].push(mid);
  });

  var dates = Object.keys(rowsByDate);
  if (dates.length === 0) return {};
  var minDate = dates.reduce(function (a, b) { return b < a ? b : a; });
  var maxDate = dates.reduce(function (a, b) { return b > a ? b : a; });
  var startYear = parseInt(minDate.substring(0, 4), 10);
  var endYear = parseInt(maxDate.substring(0, 4), 10);

  var out = {};
  HOLIDAY_KEYS.forEach(function (key) {
    var spikePcts = [];
    var dipPcts = [];
    for (var year = startYear; year <= endYear; year++) {
      var d = computeHolidayDate(key, year);
      if (!d) continue;
      var baseline = windowAvgPrice(rowsByDate, addDays(d, -45), addDays(d, -15));
      var baseline2 = windowAvgPrice(rowsByDate, addDays(d, 15), addDays(d, 45));
      var baselineAvg = average([baseline, baseline2].filter(function (v) { return v != null; }));
      if (baselineAvg == null || baselineAvg === 0) continue;
      var pre = windowAvgPrice(rowsByDate, addDays(d, -14), addDays(d, -1));
      var post = windowAvgPrice(rowsByDate, d, addDays(d, 14));
      if (pre != null) spikePcts.push((pre - baselineAvg) / baselineAvg * 100);
      if (post != null) dipPcts.push((post - baselineAvg) / baselineAvg * 100);
    }
    var yearsUsed = Math.min(spikePcts.length, dipPcts.length);
    if (spikePcts.length === 0 && dipPcts.length === 0) return; // no data at all — leave key absent, caller falls back
    out[key] = {
      spikePct: spikePcts.length ? round1(average(spikePcts)) : null,
      dipPct: dipPcts.length ? round1(average(dipPcts)) : null,
      yearsUsed: yearsUsed,
      isReal: yearsUsed >= MIN_YEARS,
    };
  });
  return out;
}
